import { useState, useEffect } from "react";
import type { DisplayOptionType } from "../utils/compareTypes";
import { useCompareV2Selector } from "../redux/store";
import { saveDisplayOptions, getCompareDisplayOptions } from "../utils/localStorage";

type DataType = {
    onlyShowDiff: boolean;
    setOnlyShowDiff: React.Dispatch<boolean>;
    showDiffToggle: boolean;
};

/**
 * Hook to help manage onlyShowDifferences(Car / Engine) settings
 * - Setting value will persist to localstorage
 * - Getting the setting is overridden by query params and the amount of cars (Always show everything when only one car is visible)
 */
const useDisplayOptions = (type: DisplayOptionType["type"]): DataType => {
    const carsCount = useCompareV2Selector((state) => state.newCarCompare.grades.length);
    const settings = useCompareV2Selector((state) => state.commonSettings);
    const compareContext = useCompareV2Selector((state) => state.newCarCompare.ncCompareContext);
    const displayOptions = getCompareDisplayOptions(settings, compareContext);
    const diffLocalSetting = displayOptions[type]; // get from localstorage
    const diffQuerySetting = // get from query
        type === "Car" ? settings.query.onlyShowDifferencesCar : settings.query.onlyShowDifferencesEngine;

    // query string should override localstorage setting in both cases
    // that is why we first check diffQuerySetting === false (otherwise it might fallback to localstorage anyways)
    const [onlyShowDiff, setOnlyShowDiff] = useState(
        diffQuerySetting !== undefined || diffLocalSetting !== undefined ? diffQuerySetting || diffLocalSetting : true,
    );

    // Always show everything when only one car is visible
    const [forceShowAll, setForceShowAll] = useState(carsCount < 2);
    useEffect(() => {
        setForceShowAll(carsCount < 2);
    }, [carsCount]);

    // Persist selected option
    useEffect(() => {
        saveDisplayOptions(
            {
                type,
                onlyShowDifferences: onlyShowDiff,
            },
            settings,
            compareContext,
        );
    }, [onlyShowDiff]);

    return {
        onlyShowDiff: !forceShowAll && onlyShowDiff,
        setOnlyShowDiff,
        showDiffToggle: carsCount > 1,
    };
};

export default useDisplayOptions;
